import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import DOMPurify from "dompurify";
import ArcGISExportLayer from "./ArcGISExportLayer";
import { makeRepertoPin, makeMyMapsPin } from "./SwissTopoMap";
import "../lib/map.css";

/**
 * Slovenia map — mirrors the ARSO eVode atlas
 * (https://gis.arso.gov.si/evode/profile.aspx?id=atlas_voda_Lidar@Arso).
 *
 * Three stacked layers — toggleable independently, exactly like eVode:
 *   1. PREGLEDNE — greyscale topographic map (native EPSG:3857 tile cache,
 *      fast at every zoom level). Drawn first, acts as a fallback canvas.
 *   2. DOF 2022-24 — most recent ortophoto (EPSG:3794 cache, reprojected
 *      per tile via ArcGIS `export`). Heavy below z=10 so capped at z≥10.
 *      Default ON — the "principale" layer the user sees on open.
 *   3. LIDAR — hillshade rilievo del terreno (same export pipeline, z≥10).
 *
 * Image tiles use `<img>` directly against gis.arso.gov.si — `<img>` is
 * not subject to CORS for display, so we don't need the backend proxy
 * (which was the bottleneck for parallel tile loads).
 */

const ARSO = "https://gis.arso.gov.si/arcgis/rest/services";
const ARSO_ATTR = '<a href="https://gis.arso.gov.si" target="_blank" rel="noreferrer">© ARSO</a>';

const SLO_CENTER = [46.15, 14.99]; // approx. centre of Slovenia
const SLO_DEFAULT_ZOOM = 9;

// Minimum zoom at which the DOF/Lidar ArcGIS export endpoints return in
// reasonable time (verified empirically — below this ARSO times out
// because the export call must reproject huge areas from EPSG:3794).
const HIGH_ZOOM_MIN = 10;

// All three layers in display order (bottom → top).
const LAYERS = [
  {
    id: "pregledne",
    label: "Pregledne — Carte topografiche",
    type: "tile",
    url: `${ARSO}/Topografske_karte_ARSO_siva_EPSG3857/MapServer/tile/{z}/{y}/{x}`,
    attribution: ARSO_ATTR,
    defaultOn: true,
    defaultOpacity: 1,
    group: "Sfondo",
  },
  {
    id: "dof2024",
    label: "Ortofoto DOF 2022-24",
    type: "arcgis-export",
    url: `${ARSO}/DOF_D96TM_2022_2023_2024/MapServer`,
    attribution: `DOF 2022-24 ${ARSO_ATTR} / GURS`,
    minZoom: HIGH_ZOOM_MIN,
    defaultOn: true,
    defaultOpacity: 1,
    group: "Sfondo",
    note: "Visibile da zoom 10 in poi",
  },
  {
    id: "lidar",
    label: "Lidar — Rilievo del terreno",
    type: "arcgis-export",
    url: `${ARSO}/Lidar_hillshade_D96TM/MapServer`,
    attribution: `LIDAR ${ARSO_ATTR}`,
    minZoom: HIGH_ZOOM_MIN,
    defaultOn: false,
    defaultOpacity: 0.6,
    group: "Rilievo",
    note: "Visibile da zoom 10 in poi",
  },
];

// ---------------------------------------------------------------
// Layer switcher — same .layer-switcher styles as SwissTopoMap so
// the panel matches the Swiss UI exactly (colours, typography, etc.)
// ---------------------------------------------------------------
const LayerSwitcher = ({ activeIds, toggle, opacities, setOpacity }) => {
  const [collapsed, setCollapsed] = useState(false);
  const grouped = {};
  LAYERS.forEach((l) => {
    grouped[l.group] = grouped[l.group] || [];
    grouped[l.group].push(l);
  });
  return (
    <div className={`layer-switcher${collapsed ? " collapsed" : ""}`} data-testid="layer-switcher">
      <button
        type="button"
        className="switcher-toggle"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="layer-switcher-toggle"
      >
        <span>Strati{activeIds.length > 0 ? ` · ${activeIds.length}` : ""}</span>
        <span className="toggle-icon">▾</span>
      </button>
      <div className="switcher-body">
        {Object.entries(grouped).map(([group, layers]) => (
          <React.Fragment key={group}>
            <div className="group-title">{group}</div>
            {layers.map((l) => {
              const active = activeIds.includes(l.id);
              const op = opacities[l.id] ?? l.defaultOpacity ?? 1;
              return (
                <div key={l.id} className="layer-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(l.id)}
                      data-testid={`slo-layer-${l.id}`}
                    />
                    <span>{l.label}</span>
                  </label>
                  {l.note && (
                    <div className="text-[9px] mt-0.5 ml-5 leading-tight" style={{ color: "var(--text-muted)" }}>
                      {l.note}
                    </div>
                  )}
                  {active && (
                    <div className="opacity-row">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(op * 100)}
                        onChange={(e) => setOpacity(l.id, parseInt(e.target.value, 10) / 100)}
                        className="opacity-slider"
                        data-testid={`slo-opacity-${l.id}`}
                      />
                      <span className="font-mono w-9 text-right">{Math.round(op * 100)}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ClickHandler = ({ enabled, onPick }) => {
  useMapEvents({
    click(e) {
      if (enabled && onPick) onPick(e.latlng);
    },
  });
  return null;
};

const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 14, { duration: 0.8 });
    // eslint-disable-next-line
  }, [position?.[0], position?.[1]]);
  return null;
};

// Render the picked layer. Plain TileLayer for native 3857 caches,
// ArcGISExportLayer for EPSG:3794 caches (server-side reprojection).
const renderLayer = (cfg, opacity) => {
  if (cfg.type === "arcgis-export") {
    return (
      <ArcGISExportLayer
        key={cfg.id}
        url={cfg.url}
        opacity={opacity}
        minZoom={cfg.minZoom || 0}
        attribution={cfg.attribution}
      />
    );
  }
  return (
    <TileLayer
      key={cfg.id}
      url={cfg.url}
      opacity={opacity}
      minZoom={cfg.minZoom || 0}
      attribution={cfg.attribution}
      maxZoom={19}
    />
  );
};

// ---------------------------------------------------------------
// Main component. Prop-compatible with SwissTopoMap so MapPage /
// LocationPicker swap them via the country selector without other
// changes.
// ---------------------------------------------------------------
export default function SloveniaMap({
  center = SLO_CENTER,
  zoom = SLO_DEFAULT_ZOOM,
  markers = [],
  myMapsPoints = [],
  height = 460,
  showLayerSwitcher = true,
  pickable = false,
  onPick,
  pickedPosition = null,
  flyTo = null,
}) {
  const [activeIds, setActiveIds] = useState(
    LAYERS.filter((l) => l.defaultOn).map((l) => l.id),
  );
  const [opacities, setOpacities] = useState(() => {
    const init = {};
    LAYERS.forEach((l) => { init[l.id] = l.defaultOpacity ?? 1; });
    return init;
  });
  const setOpacity = (id, v) => setOpacities((s) => ({ ...s, [id]: v }));
  const toggle = (id) =>
    setActiveIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Render layers in declared order, bottom → top, so DOF sits above
  // Pregledne and Lidar sits above DOF.
  const activeLayers = LAYERS.filter((l) => activeIds.includes(l.id));

  return (
    <div className="relative" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        {activeLayers.map((l) => renderLayer(l, opacities[l.id] ?? l.defaultOpacity ?? 1))}

        <ClickHandler enabled={pickable} onPick={onPick} />
        {flyTo && <FlyTo position={flyTo} zoom={14} />}

        {markers.map((m, i) => (
          <Marker
            key={`${m.code || "m"}-${m.position[0]}-${m.position[1]}-${i}`}
            position={m.position}
            icon={m.code ? makeRepertoPin(m.code) : undefined}
            eventHandlers={m.onClick ? { click: m.onClick } : undefined}
          >
            {m.popup && <Popup>{m.popup}</Popup>}
          </Marker>
        ))}

        {myMapsPoints.map((p, i) => (
          <Marker
            key={`mm-${p.point_id || p.layer_id || "x"}-${p.lat}-${p.lng}-${i}`}
            position={[p.lat, p.lng]}
            icon={makeMyMapsPin(p.color || "#9C5B42")}
          >
            <Popup>
              <div className="min-w-[180px]">
                {p.layer_name && (
                  <div className="font-mono text-[10px] tracking-wider uppercase text-[#9C5B42] mb-1">
                    {p.layer_name}
                  </div>
                )}
                <div className="font-serif text-base text-[#1E2520]">{p.name || "Punto"}</div>
                {p.description && (
                  <div className="text-xs text-[#5C635A] mt-1 whitespace-pre-wrap">
                    {DOMPurify.sanitize(p.description, { ALLOWED_TAGS: [] })}
                  </div>
                )}
                {p.is_personal && (p.onEdit || p.onDelete) && (
                  <div className="flex gap-1.5 mt-2.5 pt-2.5 border-t border-[#E5E0D8]">
                    {p.onEdit && (
                      <button
                        type="button"
                        onClick={p.onEdit}
                        className="text-[11px] text-[#9C5B42] hover:underline"
                        data-testid="mymaps-popup-edit"
                      >
                        Modifica
                      </button>
                    )}
                    {p.onDelete && (
                      <button
                        type="button"
                        onClick={p.onDelete}
                        className="text-[11px] text-red-700 hover:underline ml-auto"
                        data-testid="mymaps-popup-delete"
                      >
                        Elimina
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {pickedPosition && <Marker position={pickedPosition} />}
      </MapContainer>

      {showLayerSwitcher && (
        <LayerSwitcher
          activeIds={activeIds}
          toggle={toggle}
          opacities={opacities}
          setOpacity={setOpacity}
        />
      )}
    </div>
  );
}
